import { SavedTender, Tender } from '../models/tender.model';
import { daysFromNow } from './date.util';

const m = (label: string) => ({ label, kind: 'match' as const });
const w = (label: string) => ({ label, kind: 'warning' as const });

export const TENDERS_MOCK: Tender[] = [
  {
    id: 't-1001',
    title: 'VMware Infrastructure Operations',
    authority: 'State Office for Digitalisation, Bavaria',
    description:
      'Operation and further development of the central VMware vSphere environment across two data center sites.',
    published: daysFromNow(-9),
    deadline: daysFromNow(19),
    region: 'Bavaria',
    estimatedValueEur: 1_800_000,
    cpv: '72610000-9',
    cpvLabel: 'Computer support services',
    category: 'Managed Services',
    technologies: ['VMware', 'vSphere', 'NSX', 'Ansible'],
    signals: [
      m('VMware'),
      m('Datacenter'),
      m('Automation'),
      m('Region Bavaria'),
      w('24x7 operation mentioned'),
      w('Certification requirements may apply'),
    ],
    status: 'open',
    procedure: 'Open procedure (EU-wide)',
    contractDuration: '36 months + 2 × 12 months option',
    overview:
      'The contracting authority is looking for a service provider to operate and further develop its central virtualization platform based on VMware vSphere and NSX across two data center sites. Scope includes patch and lifecycle management, capacity planning, automation of recurring tasks and second-level support for the platform. A structured handover from the incumbent provider is planned for the first three months.',
    concerns: [
      'Possible 24x7 requirement for platform incidents',
      'Certification requirements (security, VMware) are likely',
      'Reference projects of comparable size may be required',
    ],
    portalName: 'Vergabeportal Bayern',
  },
  {
    id: 't-1002',
    title: 'Datacenter Managed Services',
    authority: 'Stadtwerke Stuttgart IT GmbH',
    description: 'Managed services for server, storage and network infrastructure in a municipal data center.',
    published: daysFromNow(-4),
    deadline: daysFromNow(33),
    region: 'Baden-Württemberg',
    estimatedValueEur: 3_200_000,
    cpv: '72500000-0',
    cpvLabel: 'Computer-related services',
    category: 'Managed Services',
    technologies: ['Datacenter', 'Windows Server', 'Linux', 'Storage'],
    signals: [
      m('Datacenter'),
      m('Linux'),
      m('Windows Server'),
      m('Region Baden-Württemberg'),
      w('24x7 operation mentioned'),
      w('Value above preferred maximum'),
    ],
    status: 'open',
    procedure: 'Open procedure (EU-wide)',
    contractDuration: '48 months',
    overview:
      'Operation of server, storage and network infrastructure in a municipal data center, including monitoring, incident and change management. Bidders are expected to provide a service desk for infrastructure incidents and monthly service reporting.',
    concerns: [
      'Mandatory 24x7 operation appears in the notice',
      'Estimated value exceeds your preferred contract value',
      'ISO 27001 may be requested',
    ],
    portalName: 'Vergabeplattform Baden-Württemberg',
  },
  {
    id: 't-1003',
    title: 'Storage Infrastructure Renewal',
    authority: 'Bavarian State Ministry of Health',
    description: 'Renewal of the central storage infrastructure including migration and handover to operations.',
    published: daysFromNow(-15),
    deadline: daysFromNow(6),
    region: 'Bavaria',
    estimatedValueEur: 950_000,
    cpv: '48820000-2',
    cpvLabel: 'Servers',
    category: 'Infrastructure',
    technologies: ['Storage', 'Datacenter', 'Migration'],
    signals: [m('Storage'), m('Datacenter'), m('Region Bavaria'), w('Hardware delivery may be part of the scope')],
    status: 'closing-soon',
    procedure: 'Restricted procedure',
    contractDuration: '12 months implementation, 5 years support',
    overview:
      'Replacement of the central storage systems including data migration and subsequent operation. The tender combines hardware delivery with implementation and support services.',
    concerns: ['Hardware delivery may be part of the scope', 'Short remaining time until the deadline'],
    portalName: 'Vergabeportal Bayern',
  },
  {
    id: 't-1004',
    title: 'IT Automation Services',
    authority: 'Federal Agency for Digital Administration',
    description: 'Framework agreement for automating recurring operations processes with Ansible and infrastructure as code.',
    published: daysFromNow(-2),
    deadline: daysFromNow(41),
    region: 'Germany remote',
    estimatedValueEur: 620_000,
    cpv: '72212000-4',
    cpvLabel: 'Programming services of application software',
    category: 'Automation',
    technologies: ['Ansible', 'AWX', 'Automation', 'Linux'],
    signals: [m('Ansible'), m('AWX'), m('Automation'), m('Remote work possible')],
    status: 'open',
    procedure: 'Open procedure (EU-wide)',
    contractDuration: '24 months + 24 months option',
    overview:
      'Framework agreement for automating recurring operations tasks using Ansible and infrastructure-as-code practices. Call-offs are made for individual automation packages. Work can largely be delivered remotely.',
    concerns: [
      'Framework agreements may require several comparable references',
      'Security clearance for staff could be requested',
    ],
    portalName: 'evergabe-online',
  },
  {
    id: 't-1005',
    title: 'Kubernetes Platform Operations',
    authority: 'Municipal Information Processing Association',
    description: 'Set-up and operation of a Kubernetes platform for municipal specialist applications.',
    published: daysFromNow(-6),
    deadline: daysFromNow(27),
    region: 'Baden-Württemberg',
    estimatedValueEur: 1_250_000,
    cpv: '72611000-6',
    cpvLabel: 'Technical computer support services',
    category: 'Managed Services',
    technologies: ['Kubernetes', 'Linux', 'Automation'],
    signals: [m('Kubernetes'), m('Linux'), m('Automation'), w('Certification requirements may apply')],
    status: 'open',
    procedure: 'Open procedure (EU-wide)',
    contractDuration: '36 months',
    overview:
      'Set-up and operation of a Kubernetes platform for municipal specialist applications, including CI/CD integration, observability and platform support for development teams.',
    concerns: [
      'CKA/CKAD or comparable certifications may be required',
      'Reference projects with Kubernetes in production may be required',
    ],
    portalName: 'Vergabeplattform Baden-Württemberg',
  },
  {
    id: 't-1006',
    title: 'Network & Security Operations',
    authority: 'Free State of Saxony, SID',
    description: 'Operation of network and security components (firewalls, switching, WLAN) for state agencies.',
    published: daysFromNow(-11),
    deadline: daysFromNow(12),
    region: 'Saxony',
    estimatedValueEur: 2_400_000,
    cpv: '72700000-7',
    cpvLabel: 'Computer network services',
    category: 'Managed Services',
    technologies: ['Network', 'Firewall', 'WLAN'],
    signals: [
      m('Network'),
      w('Region outside preferred regions'),
      w('24x7 operation mentioned'),
      w('Security clearance may apply'),
    ],
    status: 'open',
    procedure: 'Open procedure (EU-wide)',
    contractDuration: '48 months',
    overview:
      'Operation of network and security components for state agencies, including firewall rule management, switching and wireless infrastructure.',
    concerns: [
      'Region is outside your preferred regions',
      '24x7 operation is mentioned',
      'ISO 27001 and security clearance may be required',
    ],
    portalName: 'Vergabemarktplatz Sachsen',
  },
];

export const SAVED_TENDERS_MOCK: SavedTender[] = [
  { tenderId: 't-1001', savedAt: daysFromNow(-3), archived: false },
  { tenderId: 't-1003', savedAt: daysFromNow(-5), archived: false },
  { tenderId: 't-1004', savedAt: daysFromNow(-1), archived: false },
];
